import { spriteAnimDurationMs } from '../components/BattleSprite';

export function createMonsterInstance(monster, id, side, slotIndex) {
  const hpMin = monster.hpMin || 100;
  const hpMax = monster.hpMax || hpMin + 100;
  const monsterHP = Math.floor(Math.random() * (hpMax - hpMin + 1)) + hpMin;
  return {
    id,
    monsterId: monster.id,
    name: monster.name,
    level: monster.level,
    hp: monsterHP,
    maxHp: monsterHP,
    attackMin: monster.attackMin,
    attackMax: monster.attackMax,
    defense: monster.defense,
    exp: monster.exp,
    goldMin: monster.goldMin,
    goldMax: monster.goldMax,
    attackSpeed: monster.attackSpeed || 1,
    attackSound: monster.attackSound || 'sounds/monsters/default_attack.mp3',
    battleSprites: monster.battleSprites || null,
    side,
    originSide: side,
    slotIndex,
    anim: 'run',
    knockbackPx: 0,
    knockbackUntil: 0,
    knockbackMoveMs: 0,
    knockbackOutMs: 0,
    spawnPhase: 'approaching',
  };
}

export function isMetinEncounter(monster) {
  return String(monster?.encounterKind || '').toLowerCase() === 'metin';
}

export const METIN_STONE_FRAME_SIZE = 100;
export const METIN_STONE_FRAME_COUNT = 5;
export const METIN_STONE_SHEET_HEIGHT = 300;
export const METIN_ROW_DROP = 0;
export const METIN_ROW_IDLE = 1;
export const METIN_ROW_DEATH = 2;
export const METIN_IDLE_FPS = 6;
export const METIN_DROP_FPS = 8;
export const METIN_PREDROP_MS = 1000;
export const METIN_DEATH_LEAD_MS = 1000;
export const METIN_DEATH_HOLD_MS = 2000;
export const METIN_DROP_SOUND = 'sounds/stone/dusme.mp3';
export const METIN_BREAK_SOUND = 'sounds/stone/patlama.mp3';

export function metinStoneImageUrl(configId) {
  const id = configId || 1;
  return `image/stone/${id}.png`;
}

const metinImagePreloadCache = new Map();

export function preloadMetinStoneImage(configId) {
  const fullUrl = metinStoneImageUrl(configId).startsWith('/')
    ? metinStoneImageUrl(configId)
    : `/${metinStoneImageUrl(configId)}`;
  if (metinImagePreloadCache.has(fullUrl)) {
    return metinImagePreloadCache.get(fullUrl);
  }
  const promise = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = fullUrl;
  });
  metinImagePreloadCache.set(fullUrl, promise);
  return promise;
}

/** Metin taşı sprite sheet — image/stone/{configId}.png, 3 satır × 5 kare */
export function metinStoneSheet(configId, frameRow, fps = 10) {
  return {
    url: metinStoneImageUrl(configId),
    frameWidth: METIN_STONE_FRAME_SIZE,
    frameHeight: METIN_STONE_FRAME_SIZE,
    frameCount: METIN_STONE_FRAME_COUNT,
    frameRow,
    sheetHeight: METIN_STONE_SHEET_HEIGHT,
    fps,
  };
}

export function metinConfigId(monster) {
  return monster?.encounterConfigId || monster?.monsterId || 1;
}

/** Metin taşı: karakter dibine yukarıdan düşme süresi (ms) */
export function dropDurationMs() {
  return 2400;
}

/** Metin taşı düşüş başlangıç yüksekliği (px) */
export function dropDistancePx() {
  if (typeof window === 'undefined') return 220;
  return Math.min(window.innerHeight * 0.35, 280);
}

/** Arena kenarından karaktere koşu süresi (ms) */
export function approachDurationMs(runSheet) {
  if (!runSheet) return 2800;
  return Math.max(2400, Math.min(3600, spriteAnimDurationMs(runSheet) * 6.4));
}

/** İlk geliş animasyonundaki yatay mesafe (px) */
export function approachDistancePx() {
  if (typeof window === 'undefined') return 210;
  return Math.min(window.innerWidth * 0.46, 210);
}

/** Verilen mesafeyi ilk geliş hızıyla kat etmek için süre (ms) */
export function monsterMoveDurationMs(runSheet, distancePx) {
  const approachMs = approachDurationMs(runSheet);
  const approachDist = approachDistancePx();
  const dist = Math.max(1, Math.abs(distancePx));
  return Math.round((dist / approachDist) * approachMs);
}

export function knockbackAmountPx(skill = false) {
  if (typeof window === 'undefined') return skill ? 220 : 140;
  const halfPath = Math.min(window.innerWidth * 0.38, 200);
  return skill ? Math.round(halfPath) : Math.round(halfPath * 0.62);
}

/** Karakter merkezinden dışarı doğru (sağa/sola kayma) */
export function knockbackDirection(monster) {
  return (monster.originSide || monster.side) === 'right' ? 1 : -1;
}

export function isKnockbackImmuneMonster(monster) {
  const encounterKind = String(monster?.encounterKind || '').toLowerCase();
  return encounterKind === 'metin' || encounterKind === 'boss';
}

export const SPAWN_PROTECTION_SEC = 2;

export function isSpawnProtected(monster, nowMs = Date.now()) {
  if (!monster) return false;
  const spawnedAt = monster.spawnedAt;
  if (spawnedAt) {
    return (nowMs / 1000 - spawnedAt) < SPAWN_PROTECTION_SEC;
  }
  return monster.spawnPhase === 'approaching'
    || monster.spawnPhase === 'predrop'
    || monster.spawnPhase === 'dropping';
}

export function mergeMonsterBattleState(prevList, updatedList) {
  const prevMap = new Map(prevList.map((m) => [m.id, m]));
  const updatedIds = new Set(updatedList.map((m) => m.id));
  const merged = updatedList.map((m) => {
    const p = prevMap.get(m.id);
    if (!p) {
      return {
        ...m,
        side: m.side || 'left',
        originSide: m.originSide || m.side || 'left',
        slotIndex: 0,
        anim: 'run',
        knockbackPx: 0,
        spawnPhase: 'ready',
        attackSound: m.attackSound || 'sounds/monsters/default_attack.mp3',
      };
    }
    return {
      ...p,
      ...m,
      hp: typeof m.hp === 'number' ? m.hp : p.hp,
      battleSprites: m.battleSprites ?? p.battleSprites,
      attackSound: m.attackSound || p.attackSound,
      side: p.side,
      originSide: p.originSide || p.side,
      slotIndex: p.slotIndex,
      spawnPhase: p.spawnPhase,
      knockbackPx: p.knockbackUntil > Date.now() ? p.knockbackPx : 0,
      knockbackUntil: p.knockbackUntil > Date.now() ? p.knockbackUntil : 0,
      knockbackMoveMs: p.knockbackMoveMs || 0,
      knockbackOutMs: p.knockbackOutMs || 0,
      anim:
        p.anim === 'death'
          ? 'death'
          : p.anim === 'attack'
            ? 'attack'
            : p.spawnPhase === 'approaching'
              ? 'run'
              : p.spawnPhase === 'predrop' || p.spawnPhase === 'dropping'
                ? 'idle'
                : 'idle',
    };
  });
  prevList.forEach((p) => {
    if (!updatedIds.has(p.id) && isSpawnProtected(p)) {
      merged.push(p);
    }
  });
  return merged;
}

/** Dibinde ve savrulma dışında — otomatik saldırı animasyonu için */
export function canMonsterAutoAttack(monster) {
  if (!monster || monster.hp <= 0 || monster.anim === 'death') return false;
  if (monster.spawnPhase !== 'ready') return false;
  if (Math.abs(monster.knockbackPx || 0) > 0) return false;
  if ((monster.knockbackMoveMs || 0) > 0) return false;
  return true;
}

export function damagedMonsterIds(prevList, updatedList) {
  const prevMap = new Map((prevList || []).map((m) => [m.id, m]));
  const ids = new Set();
  (updatedList || []).forEach((m) => {
    const prev = prevMap.get(m.id);
    if (prev && prev.hp > m.hp) ids.add(m.id);
  });
  return ids;
}

export function applyKnockbackToInstances(instances, amount, onlyIds = null) {
  return instances.map((m) => {
    if (onlyIds && !onlyIds.has(m.id)) return m;
    if (m.hp <= 0 || m.anim === 'death') return m;
    if (isKnockbackImmuneMonster(m)) return m;
    if (Math.abs(m.knockbackPx || 0) > 0) return m;
    const isAtFeet = m.spawnPhase === 'ready';
    const isAttacking = m.anim === 'attack';
    if (!isAtFeet && !isAttacking) return m;
    const dir = knockbackDirection(m);
    const moveMs = monsterMoveDurationMs(m.battleSprites?.run, amount);
    const outMs = 260;
    return {
      ...m,
      knockbackPx: dir * amount,
      knockbackUntil: Date.now() + outMs + moveMs,
      knockbackMoveMs: moveMs,
      knockbackOutMs: outMs,
    };
  });
}

export function maxKnockbackMoveMs(instances) {
  let max = 0;
  for (const m of instances || []) {
    if (m.knockbackMoveMs > max) max = m.knockbackMoveMs;
  }
  return max;
}

export function maxKnockbackOutMs(instances) {
  let max = 0;
  for (const m of instances || []) {
    if (m.knockbackOutMs > max) max = m.knockbackOutMs;
  }
  return max || 260;
}

export function knockedMonsterIds(instances) {
  return new Set(
    (instances || []).filter((m) => Math.abs(m.knockbackPx || 0) > 0).map((m) => m.id)
  );
}

export function deathAnimDurationMs(battleSprites, monster = null) {
  if (monster && isMetinEncounter(monster)) {
    return METIN_DEATH_LEAD_MS + METIN_DEATH_HOLD_MS;
  }
  const sheet = battleSprites?.death;
  if (!sheet) return 1600;
  const fc = sheet.frameCount || 5;
  const fps = sheet.fps || 8;
  return Math.max(400, Math.round((fc / fps) * 1000));
}
